import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";
import "./MakeBeeTripChatbot.css";
import ReactMarkdown from "react-markdown";
import { useAIChat } from "./AIChatContext";
import { useAuth } from "../../AuthProvider/AuthContext";
import Logo from "../../assets/logo1.png";

// Stable session id so every message in one browser session
// maps to the same ai_conversations row on the server.
function getOrCreateSessionId() {
  try {
    let sessionId = sessionStorage.getItem("makebeetrip-ai-session-id");

    if (!sessionId) {
      sessionId = `web_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 10)}`;

      sessionStorage.setItem("makebeetrip-ai-session-id", sessionId);
    }

    return sessionId;
  } catch {
    // sessionStorage unavailable → the server will generate one per request.
    return null;
  }
}

export default function MakeBeeTripChatbot() {
  const {
    open,
    setOpen,
    initialPrompt,
    setInitialPrompt,
  } = useAIChat();

  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  // Server-side conversation tracking (ai_conversations.id).
  // A ref (not state) so changing it never triggers a re-render.
  const currentConversationId = useRef(null);

  const buttonRef = useRef(null);
  const dragRef = useRef(null);
  const { user, token } = useAuth();

  const [position, setPosition] = useState({
    x: null,
    y: null,
  });

  // --------------------------------------------------
  // Restore chatbot button position
  // --------------------------------------------------

  useEffect(() => {
    const saved = localStorage.getItem("makebeetrip-ai-position");

    if (saved) {
      try {
        const parsed = JSON.parse(saved);

        if (
          typeof parsed.x === "number" &&
          typeof parsed.y === "number"
        ) {
          setPosition(parsed);
        }
      } catch {
        // Ignore invalid saved position.
      }
    }
  }, []);

  // --------------------------------------------------
  // Save floating button position
  // --------------------------------------------------

  const savePosition = (nextPosition) => {
    setPosition(nextPosition);

    localStorage.setItem(
      "makebeetrip-ai-position",
      JSON.stringify(nextPosition)
    );
  };

  // --------------------------------------------------
  // Drag handling
  // --------------------------------------------------

  const handlePointerDown = (event) => {
    if (open) return;

    event.preventDefault();

    const rect = buttonRef.current?.getBoundingClientRect();

    if (!rect) return;

    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      initialX: rect.left,
      initialY: rect.top,
    };

    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current || open) return;

    const {
      startX,
      startY,
      initialX,
      initialY,
    } = dragRef.current;

    const buttonSize = 58;

    const nextX = Math.min(
      Math.max(0, initialX + event.clientX - startX),
      window.innerWidth - buttonSize
    );

    const nextY = Math.min(
      Math.max(0, initialY + event.clientY - startY),
      window.innerHeight - buttonSize
    );

    savePosition({
      x: nextX,
      y: nextY,
    });
  };

  const handlePointerUp = () => {
    dragRef.current = null;
  };

  // --------------------------------------------------
  // Send message
  // --------------------------------------------------

  const sendMessageText = async (text) => {
    const trimmedText = text.trim();

    if (!trimmedText || loading) return;

    // IMPORTANT:
    // Send the conversation that existed BEFORE
    // adding the current message.
    const conversationHistory = messages.map((item) => ({
      role: item.role,
      content: item.text,
    }));

    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        text: trimmedText,
      },
    ]);

    setMessage("");
    setLoading(true);

    try {
      // Attach the customer JWT when logged in so conversations are
      // linked to users.id; guests simply send no Authorization header.
      const headers = {
        "Content-Type": "application/json",
      };

      if (user && token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const API_BASE =
  (process.env?.REACT_APP_API_URL || "http://localhost:5000").replace(/\/$/, "");

const response = await fetch(
  `${API_BASE}/api/ai/chat`,
  {
          method: "POST",
          headers,
          body: JSON.stringify({
            message: trimmedText,
            history: conversationHistory,
            sessionId: getOrCreateSessionId(),
            conversationId: currentConversationId.current || undefined,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.error || "AI request failed"
        );
      }

      // Remember the conversation id the server created/reused so
      // subsequent messages stay in the same conversation.
      if (data.conversationId) {
        currentConversationId.current = data.conversationId;
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text:
            data.reply ||
            "Sorry, I couldn't generate a response.",
        },
      ]);
    } catch (error) {
      console.error("MakeBeeTrip AI:", error);

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text:
            "Sorry, MakeBeeTrip AI is temporarily unavailable. Please try again.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

    // --------------------------------------------------
  // Open chatbot from Hero with an initial prompt
  // --------------------------------------------------

  useEffect(() => {
    if (!open || !initialPrompt || loading) return;

    const prompt = initialPrompt.trim();

    if (!prompt) {
      setInitialPrompt("");
      return;
    }

    setInitialPrompt("");
    sendMessageText(prompt);
  }, [open, initialPrompt, loading, setInitialPrompt]);

  const sendMessage = async (event) => {
    event.preventDefault();

    const text = message.trim();

    if (!text || loading) return;

    await sendMessageText(text);
  };

  // --------------------------------------------------
  // Floating button position
  // --------------------------------------------------

  const buttonStyle =
    position.x !== null && position.y !== null
      ? {
          left: `${position.x}px`,
          top: `${position.y}px`,
          right: "auto",
          bottom: "auto",
        }
      : {};

  // --------------------------------------------------
  // UI
  // --------------------------------------------------

  return (
    <>
      {!open && (
        <button
  ref={buttonRef}
  type="button"
  className="makebeetrip-ai-fab"
  style={buttonStyle}
  onClick={() => setOpen(true)}
  onPointerDown={handlePointerDown}
  onPointerMove={handlePointerMove}
  onPointerUp={handlePointerUp}
  aria-label="Open MakeBeeTrip AI"
  title="MakeBeeTrip AI"
>
  <svg
  className="makebeetrip-ai-fab__text"
  viewBox="0 0 120 55"
  aria-hidden="true"
>
  <defs>
    <path
  id="makebeetrip-chatbot-arc"
  d="M -15 62 C 5 -20, 115 -20, 135 62"
  fill="none"
/>
  </defs>

  <text>
  <textPath
    href="#makebeetrip-chatbot-arc"
    startOffset="50%"
    textAnchor="middle"
  >
    <tspan fill="#111827">Make</tspan>
    <tspan fill="#087f5b">Bee</tspan>
    <tspan fill="#111827">Trip AI</tspan>
  </textPath>
</text>
</svg>

<div className="makebeetrip-ai-fab__logo-wrap">
  <img
    src={Logo}
    alt="MakeBeeTrip"
    className="makebeetrip-ai-fab__logo"
  />
</div>
</button>
      )}

      {open && (
        <div className="makebeetrip-ai-overlay">
          <button
            type="button"
            className="makebeetrip-ai-backdrop"
            onClick={() => setOpen(false)}
            aria-label="Close MakeBeeTrip AI"
          />

          <div className="makebeetrip-ai-panel">
            {/* Header */}
            <div className="makebeetrip-ai-header">
              <div className="makebeetrip-ai-brand">
                <span>
                  <MessageCircle size={18} />
                </span>

                <div>
                  <strong>MakeBeeTrip AI</strong>
                  <small>Travel assistant</small>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <X size={19} />
              </button>
            </div>

            {/* Messages */}
            <div className="makebeetrip-ai-messages">
              {messages.length === 0 && (
                <div className="makebeetrip-ai-welcome">
                  <strong>
                    How can I help you travel?
                  </strong>

                  <p>
                    Ask me about flights, hotels, buses,
                    holiday packages, visa enquiries or
                    trip planning.
                  </p>
                </div>
              )}

              {messages.map((item, index) => (
                <div
                  key={`${item.role}-${index}`}
                  className={`makebeetrip-ai-message makebeetrip-ai-message--${item.role}`}
                >
                  {item.role === "assistant" ? (
                    <ReactMarkdown
                      components={{
                        a: ({
                          href,
                          children,
                        }) => (
                          <a
                            href={href}
                            className="makebeetrip-ai-action"
                            target="_self"
                            rel="noopener noreferrer"
                          >
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {item.text}
                    </ReactMarkdown>
                  ) : (
                    item.text
                  )}
                </div>
              ))}

              {loading && (
                <div className="makebeetrip-ai-message makebeetrip-ai-message--assistant makebeetrip-ai-thinking">
                

                  <span
                    className="makebeetrip-ai-dots"
                    aria-hidden="true"
                  >
                    <span>●</span>
                    <span>●</span>
                    <span>●</span>
                  </span>
                </div>
              )}
            </div>

            {/* Composer */}
            <form
              className="makebeetrip-ai-composer"
              onSubmit={sendMessage}
            >
              <input
                value={message}
                onChange={(event) =>
                  setMessage(event.target.value)
                }
                placeholder="Ask MakeBeeTrip AI..."
              />

              <button
                type="submit"
                disabled={!message.trim() || loading}
                aria-label="Send"
              >
                <Send size={17} />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}